import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Minus, ShoppingCart, Edit3, FileText, Search } from 'lucide-react';

import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatAmount } from '../../utils/format';
import { calcTotals, itbisLabel } from '../../utils/tax';
 
const CreateOrderModal = ({ tableId, tableNumber, isOpen, onClose, onOrderCreated, tenant }) => {
  const { getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [notes, setNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(null); // ID del producto editando notas
  const [loading, setLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  
  // Estados para variantes
  const [showVariantSelector, setShowVariantSelector] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Estados para crear nuevo cliente
  const [showCreateCustomerDialog, setShowCreateCustomerDialog] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadCustomers();
    }
  }, [isOpen]);

  // Cargar productos después de que las categorías estén listas
  useEffect(() => {
    if (isOpen && categories.length > 0) {
      loadProducts();
    }
  }, [isOpen, categories]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: getAuthHeaders()
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: getAuthHeaders()
      });
      
      // Filtrar productos disponibles
      const availableProducts = (Array.isArray(response.data) ? response.data : []).filter(p => p.is_available);
      
      // Excluir productos de categoría Delivery
      const deliveryCategory = categories.find(c => c.name.toLowerCase() === 'delivery');
      if (deliveryCategory) {
        setProducts(availableProducts.filter(p => p.category_id !== deliveryCategory.id));
      } else {
        setProducts(availableProducts);
      }
    } catch {
      toast.error('Error al cargar productos');
    }
  };

  const loadCustomers = async () => {
    try {
      console.info('🔍 Cargando clientes...');
      const response = await axios.get(`${API}/customers`, {
        headers: getAuthHeaders()
      });
      console.info('✅ Clientes recibidos:', response.data);
      console.info('  Total:', response.data.length);
      setCustomers(response.data);
    } catch (error) {
      console.error('❌ Error loading customers:', error);
      console.error('  Response:', error.response?.data);
      console.error('  Status:', error.response?.status);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerPhone(customer.phone || '');
    setCustomerSearch('');
    setShowCustomerList(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerSearch('');
  };
  
  const openCreateCustomerDialog = () => {
    // Pre-llenar con el nombre que estaba buscando
    setNewCustomerData({
      name: customerSearch,
      phone: '',
      email: ''
    });
    setShowCustomerList(false);
    setShowCreateCustomerDialog(true);
  };
  
  const handleCreateCustomer = async () => {
    if (!newCustomerData.name || newCustomerData.name.trim() === '') {
      toast.error('El nombre del cliente es requerido');
      return;
    }
    
    try {
      // Limpiar campos vacíos antes de enviar
      const cleanData = {
        name: newCustomerData.name.trim(),
        phone: newCustomerData.phone?.trim() || null,
        email: newCustomerData.email?.trim() || null
      };
      
      const response = await axios.post(`${API}/customers`, cleanData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Cliente creado exitosamente');
      
      // Agregar el nuevo cliente a la lista y seleccionarlo
      const createdCustomer = response.data;
      setCustomers([...customers, createdCustomer]);
      selectCustomer(createdCustomer);
      
      // Cerrar dialog y resetear
      setShowCreateCustomerDialog(false);
      setNewCustomerData({ name: '', phone: '', email: '' });
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(getErrorMessage(error, 'Error al crear cliente'));
    }
  };

  const filteredCustomers = customerSearch 
    ? customers.filter(c => 
        c.name.toLowerCase().includes(customerSearch.toLowerCase())
      )
    : customers;

  const addToCart = (product) => {
    console.info('🔍 addToCart called with product:', product);
    console.info('  - Has variants?', product.variants && product.variants.length > 0);
    console.info('  - Variants:', product.variants);
    
    // Si el producto tiene variantes, mostrar selector
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
      setSelectedVariant(null);
      setShowVariantSelector(true);
      console.info('  → Opening variant selector');
      return;
    }
    
    console.info('  → Adding to cart directly (no variants)');
    // Si no tiene variantes, agregar directamente
    const existingItem = cart.find(item => item.product_id === product.id && !item.variant_id);
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.id && !item.variant_id
          ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        variant_id: null,
        variant_name: null,
        quantity: 1,
        unit_price: product.price,
        subtotal: product.price,
        notes: ''
      }]);
    }
    toast.success(`${product.name} agregado`);
  };

  const addVariantToCart = () => {
    if (!selectedVariant || !selectedProduct) return;
    
    // Crear nombre combinado: producto + variante
    const combinedName = `${selectedProduct.name} ${selectedVariant.name}`;
    
    const existingItem = cart.find(
      item => item.product_id === selectedProduct.id && item.variant_id === selectedVariant.id
    );
    
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === selectedProduct.id && item.variant_id === selectedVariant.id
          ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: selectedProduct.id,
        product_name: combinedName,
        variant_id: selectedVariant.id,
        variant_name: selectedVariant.name,
        quantity: 1,
        unit_price: selectedVariant.price,
        subtotal: selectedVariant.price,
        notes: ''
      }]);
    }
    
    toast.success(`${combinedName} agregado`);
    setShowVariantSelector(false);
    setSelectedProduct(null);
    setSelectedVariant(null);
  };

  const updateItemNotes = (productId, notes) => {
    setCart(cart.map(item =>
      item.product_id === productId
        ? { ...item, notes: notes }
        : item
    ));
  };

  const updateQuantity = (productId, change) => {
    setCart(cart.map(item =>
      item.product_id === productId
        ? { 
            ...item, 
            quantity: Math.max(0, item.quantity + change),
            subtotal: Math.max(0, item.quantity + change) * item.unit_price
          }
        : item
    ).filter(item => item.quantity > 0));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const handleCreateOrder = async () => {
    if (cart.length === 0) {
      toast.error('Agrega productos a la orden');
      return;
    }

    // Validar que haya un cliente
    if (!customerName || customerName.trim() === '') {
      toast.error('Debes seleccionar o ingresar un cliente');
      return;
    }

    setLoading(true);
    try {
      // Send simplified items (backend will enrich them)
      const simpleItems = cart.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id || null,
        quantity: item.quantity,
        notes: item.notes || ''
      }));

      const orderData = {
        order_type: 'dine_in',
        table_id: tableId,
        customer_id: selectedCustomer?.id || null,
        customer_name: customerName,
        customer_phone: customerPhone || null,
        items: simpleItems,
        notes: notes
      };

      await axios.post(`${API}/orders`, orderData, {
        headers: getAuthHeaders()
      });

      toast.success('¡Orden creada exitosamente!');
      
      // Reset form
      setCart([]);
      setCustomerName('');
      setNotes('');
      
      // Call callback if provided
      if (onOrderCreated) {
        onOrderCreated();
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(getErrorMessage(error, 'Error al crear orden'));
    }
    setLoading(false);
  };

  return (
    <>
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 30px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
            margin: 4px 0;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #fb923c;
            border-radius: 10px;
            border: 5px solid #f1f1f1;
            min-height: 60px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #f97316;
          }
        `}
      </style>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <DialogTitle>Crear Orden - Mesa {tableNumber}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Products Section */}
          <div>
            {/* Buscador de productos */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar producto..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10 rounded-xl"
                />
              </div>
            </div>
            
            <h3 className="font-semibold text-lg mb-3">Productos Disponibles</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
              {products
                .filter(product => {
                  // Ocultar productos con inventario agotado
                  if (product.tracked && (product.stock ?? 1) <= 0) return false;
                  return product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                    product.description?.toLowerCase().includes(productSearch.toLowerCase());
                })
                .map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-orange-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => addToCart(product)}
                >
                  {/* Product Image */}
                  {product.image_url ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-orange-100 to-red-100">
                      <img 
                        src={getImageUrl(product.image_url)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-8 h-8 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg></div>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                    </div>
                  )}
                  
                  {/* Product Info */}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{product.name}</p>
                    <p className="text-sm text-gray-600">{product.description ? product.description.substring(0, 40) + '...' : ''}</p>
                    {product.variants && product.variants.length > 0 ? (
                      <div className="space-y-1 mt-1">
                        {product.variants.filter(v => v.is_available).slice(0, 3).map((variant) => (
                          <div key={variant.id} className="flex justify-between items-center text-xs">
                            <span className="text-gray-700 truncate flex-1">{variant.name}</span>
                            <span className="font-semibold text-orange-600 ml-1">${formatAmount(variant.price)}</span>
                          </div>
                        ))}
                        {product.variants.filter(v => v.is_available).length > 3 && (
                          <p className="text-xs text-gray-500 italic">+{product.variants.filter(v => v.is_available).length - 3} más</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-lg font-bold text-orange-600 mt-1">${formatAmount(product.price)}</p>
                    )}
                  </div>
                  
                  {/* Add Button */}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart(product);
                    }}
                    size="sm"
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full flex-shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Cart Section */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Orden Actual
            </h3>

            <div className="space-y-4 mb-4">
              {/* Customer Selector */}
              <div>
                <Label className="flex items-center gap-1">
                  Cliente <span className="text-red-500">*</span>
                </Label>
                {!selectedCustomer ? (
                  <div className="relative">
                    <Input
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                        setShowCustomerList(true);
                      }}
                      onFocus={() => setShowCustomerList(true)}
                      onBlur={() => {
                        // Delay hiding to allow click on list items
                        setTimeout(() => setShowCustomerList(false), 200);
                      }}
                      placeholder="Buscar por nombre..."
                      className="rounded-xl"
                    />
                    {showCustomerList && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto custom-scrollbar">
                        {filteredCustomers.length > 0 ? (
                          <>
                            <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500 font-medium sticky top-0">
                              {customerSearch ? `${filteredCustomers.length} cliente(s) encontrado(s)` : `${customers.length} cliente(s) registrado(s)`}
                            </div>
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                onClick={() => selectCustomer(customer)}
                                className="p-3 hover:bg-orange-50 cursor-pointer border-b last:border-b-0 transition-colors"
                              >
                                <p className="font-semibold text-sm text-gray-800">{customer.name}</p>
                                {customer.phone && (
                                  <p className="text-xs text-gray-500">{customer.phone}</p>
                                )}
                                {customer.email && (
                                  <p className="text-xs text-gray-400">{customer.email}</p>
                                )}
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="p-4 text-center">
                            <p className="text-gray-500 text-sm mb-3">
                              {customerSearch ? 'No se encontró el cliente' : 'No hay clientes registrados'}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              onClick={openCreateCustomerDialog}
                              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl"
                            >
                              + Crear Cliente
                            </Button>
                          </div>
                        )}
                        {/* Option to add new customer */}
                        {filteredCustomers.length > 0 && (
                          <div 
                            onClick={openCreateCustomerDialog}
                            className="p-3 bg-orange-50 hover:bg-orange-100 cursor-pointer text-center text-sm font-medium text-orange-600 border-t sticky bottom-0"
                          >
                            + Agregar cliente nuevo
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{selectedCustomer.name}</p>
                      {selectedCustomer.phone && (
                        <p className="text-xs text-gray-600">{selectedCustomer.phone}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={clearCustomer}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Cambiar
                    </Button>
                  </div>
                )}
                
                {/* Manual input for new customer */}
                {!selectedCustomer && !showCustomerList && (
                  <div className="mt-2 space-y-2">
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Nombre del cliente (requerido)"
                      className="rounded-xl"
                      required
                    />
                    <Input
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="Teléfono (opcional)"
                      className="rounded-xl"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label>Notas Especiales</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: Sin cebolla, picante extra..."
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto custom-scrollbar">
              {cart.map((item) => (
                <div
                  key={item.product_id}
                  className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl space-y-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{item.product_name}</p>
                      {item.variant_name && (
                        <p className="text-xs text-orange-600 font-medium">({item.variant_name})</p>
                      )}
                      <p className="text-sm text-gray-600">${formatAmount(item.unit_price)} c/u</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.product_id, -1)}
                        className="w-8 h-8 p-0 rounded-full border-orange-300 hover:bg-orange-100"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-10 text-center font-bold text-lg text-gray-800">{item.quantity}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.product_id, 1)}
                        className="w-8 h-8 p-0 rounded-full border-orange-300 hover:bg-orange-100"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <span className="font-bold text-lg text-orange-600 min-w-[70px] text-right">
                      ${formatAmount(item.subtotal)}
                    </span>
                  </div>

                  {/* Sección de notas por producto */}
                  {editingNotes === item.product_id ? (
                    <div className="space-y-2 pl-2">
                      <Label className="text-xs text-gray-600 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Notas para este producto:
                      </Label>
                      <Input
                        value={item.notes || ''}
                        onChange={(e) => updateItemNotes(item.product_id, e.target.value)}
                        placeholder="Ej: Sin cebolla, extra picante..."
                        className="text-sm rounded-lg"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={() => setEditingNotes(null)}
                        className="w-full h-8 text-xs bg-green-500 hover:bg-green-600 rounded-lg"
                      >
                        Guardar
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2 pl-2">
                      <div className="flex-1">
                        {item.notes ? (
                          <p className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                            <FileText className="w-3 h-3 inline mr-1" />
                            {item.notes}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Sin notas</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingNotes(item.product_id)}
                        className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit3 className="w-3 h-3 mr-1" />
                        {item.notes ? 'Editar' : 'Agregar'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {cart.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p>Agrega productos a la orden</p>
                </div>
              )}
            </div>

            <div className="border-t pt-4 mb-4">
              {(() => {
                const t = calcTotals(calculateTotal(), tenant);
                return (
                  <>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">${formatAmount(t.subtotal)}</span>
                    </div>
                    {t.tax > 0 && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{itbisLabel()}:</span>
                        <span className="font-semibold">${formatAmount(t.tax)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total:</span>
                      <span className="text-orange-600">${formatAmount(t.total)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateOrder}
                disabled={loading || cart.length === 0}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                {loading ? 'Creando...' : 'Crear Orden'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
      
      {/* Dialog para crear nuevo cliente */}
      <Dialog open={showCreateCustomerDialog} onOpenChange={setShowCreateCustomerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-customer-name">
                Nombre <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-customer-name"
                value={newCustomerData.name}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                placeholder="Nombre completo"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="new-customer-phone">Teléfono</Label>
              <Input
                id="new-customer-phone"
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                placeholder="Número de teléfono"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="new-customer-email">Email (opcional)</Label>
              <Input
                id="new-customer-email"
                type="email"
                value={newCustomerData.email}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                className="rounded-xl"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateCustomerDialog(false);
                  setNewCustomerData({ name: '', phone: '', email: '' });
                }}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleCreateCustomer}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                Crear Cliente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Seleccionar Variante */}
      <Dialog open={showVariantSelector} onOpenChange={setShowVariantSelector}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Seleccionar Variante</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedProduct && (
              <>
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="font-semibold text-gray-800">{selectedProduct.name}</p>
                  <p className="text-sm text-gray-600">{selectedProduct.description}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Elige una opción:</Label>
                  {!selectedProduct.variants || selectedProduct.variants.length === 0 ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-center">
                      <p className="text-red-600 font-medium">⚠️ No hay variantes disponibles</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Este producto no tiene variantes configuradas o todas están deshabilitadas.
                      </p>
                    </div>
                  ) : (
                    selectedProduct.variants.map((variant) => {
                      const soldOut = variant.tracked === true && Number(variant.stock) <= 0;
                      const disabled = !variant.is_available || soldOut;
                      return (
                        <button
                          key={variant.id}
                          onClick={() => setSelectedVariant(variant)}
                          disabled={disabled}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                            selectedVariant?.id === variant.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-orange-300'
                          } ${
                            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-semibold text-gray-800">{variant.name}</p>
                              {!variant.is_available && (
                                <p className="text-xs text-red-500">No disponible</p>
                              )}
                              {variant.is_available && soldOut && (
                                <p className="text-xs text-red-500">Agotada</p>
                              )}
                              {variant.is_available && !soldOut && variant.tracked === true && (
                                <p className="text-xs text-gray-500">Existencia: {Number(variant.stock) || 0}</p>
                              )}
                            </div>
                            <p className="text-xl font-bold text-orange-600">
                              ${variant.price ? formatAmount(variant.price) : '0.00'}
                            </p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowVariantSelector(false)}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={addVariantToCart}
                disabled={!selectedVariant}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                Agregar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateOrderModal;
